import type { APIError } from '@vizit/api';
import { useGetRetailersForOrganizationQuery } from '@vizit/api';
import { FEATURE_FLAGS } from '@vizit/constants';
import { useFeatureFlag } from '@vizit/feature-flags';
import {
  BrandComboBox,
  ProductCategoryComboBox,
  RetailerSelect,
  ScoreLevelSelect,
  SearchTextField,
} from '@vizit/organisms';
import { cx } from '@vizit/theme';
import React from 'react';
import { useProductTable } from '../../providers';
import { useBrandList, useProductCategoryList } from './product-table-filters.hooks';

/**
 * Enum representing the available filter options for the product table
 */
enum ProductTableFilterOptions {
  ListingScoreLevel = 'score',
  Brand = 'brand',
  ProductCategory = 'categoryId',
  ProductCategoryName = 'categoryName',
  Search = 'searchTerm',
  Retailer = 'retailer',
}

interface ProductTableFiltersProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Function to handle authentication error
   *
   * @type {(error: APIError) => void}
   * @param error - The authentication error
   */
  onAuthenticationError: (error: APIError) => void;
}

/**
 * Component that renders filter controls for the product table.
 * Supports filtering by product category and listing score level.
 * Uses ProductTableProvider context for state management.
 *
 * @param props - Component props
 * @returns JSX element
 */
function ProductTableFilters(props: ProductTableFiltersProps) {
  const { className, onAuthenticationError, ...other } = props;

  const {
    organizationId,
    portfolioId,
    listingScoreLevel,
    productCategoryId,
    productCategoryName,
    brand,
    searchTerm,
    retailer,
    onFilterListingScoreLevel,
    onFilterProductCategory,
    onFilterBrand,
    onSearchTermChange,
    onFilterRetailer,
  } = useProductTable();

  const isRetailerEnabled = useFeatureFlag(FEATURE_FLAGS.RETAILER, false);

  const { data: retailers } = useGetRetailersForOrganizationQuery(
    { org_id: organizationId ?? '' },
    { enabled: isRetailerEnabled && !!organizationId },
  );

  const {
    items: productCategoryItems,
    isLoading: isLoadingProductCategory,
    filterText: productCategoryFilterText,
    setFilterText: setProductCategoryFilterText,
    loadMore: loadMoreProductCategory,
    onSelectionChange: onCategorySelectionChange,
  } = useProductCategoryList({
    organizationId,
    portfolioId,
    filterText: productCategoryName || '',
    onAuthenticationError,
    onFilterProductCategory,
  });

  const {
    items: brandItems,
    isLoading: isLoadingBrand,
    filterText: brandFilterText,
    setFilterText: setBrandFilterText,
    loadMore: loadMoreBrand,
    onSelectionChange: onBrandSelectionChange,
  } = useBrandList({
    portfolioId: portfolioId,
    filterText: brand || '',
    onAuthenticationError: onAuthenticationError,
    onFilterBrand,
  });

  const brandSelectedKey = React.useMemo(
    () => (brand ? brand.trim().toLowerCase().replace('-', ' ') : undefined),
    [brand],
  );

  return (
    <div
      className={cx('flex flex-row justify-between gap-4 [@media(max-width:1309px)]:flex-col', className)}
      {...other}
    >
      <div className="flex flex-row gap-8 pb-8 [@media(max-width:906px)]:flex-col">
        <div className="min-w-[252px]">
          <RetailerSelect
            retailers={retailers ?? []}
            selectedRetailer={retailer}
            onRetailerChange={onFilterRetailer}
            data-testid="library-products-retailer-filter"
          />
        </div>
        <div className="min-w-[252px]">
          <ProductCategoryComboBox
            filterText={productCategoryFilterText}
            setFilterText={setProductCategoryFilterText}
            items={productCategoryItems}
            selectedKey={productCategoryId}
            onSelectionChange={onCategorySelectionChange}
            isLoading={isLoadingProductCategory}
            loadMore={loadMoreProductCategory}
            data-testid="library-products-category-filter"
          />
        </div>
        <div className="min-w-[252px]">
          <BrandComboBox
            selectedKey={brandSelectedKey}
            filterText={brandFilterText}
            setFilterText={setBrandFilterText}
            items={brandItems}
            onSelectionChange={onBrandSelectionChange}
            isLoading={isLoadingBrand}
            loadMore={loadMoreBrand}
            data-testid="library-products-brand-filter"
          />
        </div>
        <div className="min-w-[252px]">
          <ScoreLevelSelect
            scoreLevel={listingScoreLevel}
            onScoreLevelChange={onFilterListingScoreLevel}
            data-testid="library-products-score-filter"
          />
        </div>
      </div>
      <div className="min-w-[300px] max-w-[600px]">
        <SearchTextField
          onSearch={onSearchTermChange}
          defaultValue={searchTerm ?? ''}
          variant="filled"
          data-testid="library-products-search"
        />
      </div>
    </div>
  );
}

export { ProductTableFilters, ProductTableFilterOptions };
